import type { ThemeOptions } from '@material-ui/core/styles';
import { createTheme } from '@material-ui/core/styles';
import { KlpFontVariant } from './KlpFontVariant';
import { KlpFonts } from './KlpFonts';
import { KlpPalette } from './KlpPalette';
import { KlpSpacing } from './KlpSpacing';
import shadows from '@material-ui/core/styles/shadows';

export const themeOptions: ThemeOptions = {
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1600,
        },
    },
    palette: KlpPalette,
    typography: KlpFonts,
    klpVariant: KlpFontVariant,
    klpSpacing: KlpSpacing,
    mainContent: {
        paddingBottom: 32,
    },
    header: {
        height: 64,
    },
    sidebar: {
        open: {
            width: 350,
            contentWidth: 210,
        },
        closed: {
            width: 70,
        },
    },
    byggSelector: {
        open: {
            width: 350,
        },
        closed: {
            width: 72,
        },
        transition: '0.35s ease-in-out',
    },
    kommentarerMenuOpen: {
        width: 500,
    },
    layout: {
        maxWidth: 'lg',
        transition: '0.35s ease-in-out',
    },
    overrides: {
        MuiCssBaseline: {
            '@global': {
                '*': {
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${KlpPalette.primary.medium} transparent`,
                },
                '*::-webkit-scrollbar': {
                    width: '6px',
                    height: '6px',
                },
                '*::-webkit-scrollbar-track': {
                    background: 'transparent',
                },
                '*::-webkit-scrollbar-thumb': {
                    backgroundColor: `${KlpPalette.primary.medium}`,
                    borderRadius: '4px',
                },
                '*:hover::-webkit-scrollbar-thumb': {
                    backgroundColor: `${KlpPalette.primary.medium}`,
                },
            },
        },
        MuiLink: {
            root: {
                '&': {
                    position: 'relative',
                    display: 'inline-block',
                },
                '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    width: '100%',
                    height: '2px',
                    bottom: '-2px',
                    left: 0,
                    background: KlpPalette.secondary.main,
                },
                '&:hover:before': {
                    background: KlpPalette.primary.main,
                },
            },
            underlineHover: {
                '&': {
                    textDecoration: 'none',
                },
                '&:hover': {
                    textDecoration: 'none',
                },
            },
        },
        MuiCard: {
            root: {
                '&': {
                    display: 'flex',
                    padding: KlpSpacing.bulky.mobile,
                    flexDirection: 'column',
                    width: '100%',
                    height: '100%',
                },
            },
        },
        MuiCardContent: {
            root: {
                paddingLeft: 0,
                paddingRight: 0,
                paddingTop: 0,
                flex: '1 1 auto',
            },
        },
        MuiCardActions: {
            spacing: {
                padding: 0,
            },
        },
        MuiButton: {
            root: {
                backgroundColor: 'transparent',

                '&:hover': {
                    backgroundColor: KlpPalette.secondary.medium,
                },
            },
            outlined: {
                transition:
                    'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,color 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                borderRadius: 0,
                borderColor: KlpPalette.primary.medium,
                '& svg': {
                    transition: 'fill 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                },
            },
            outlinedPrimary: {
                backgroundColor: KlpPalette.secondary.veryLight,
                '&:hover': {
                    color: KlpPalette.background.default,
                    backgroundColor: KlpPalette.secondary.main,
                    '& svg': {
                        fill: KlpPalette.background.default,
                    },
                },
            },
            outlinedSecondary: {
                '& svg': {
                    fill: KlpPalette.secondary.main,
                },
                '&:hover': {
                    color: KlpPalette.background.default,
                    backgroundColor: KlpPalette.secondary.main,
                    '& svg': {
                        fill: KlpPalette.background.default,
                    },
                },
            },
            contained: {
                transition:
                    'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,color 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                borderRadius: 0,
                border: '2px solid transparent',
                '&:hover': {
                    boxShadow: 'none',
                },
                '& svg': {
                    fill: KlpPalette.background.default,
                    transition: 'fill 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                },
            },
            containedPrimary: {
                '&:hover': {
                    borderColor: KlpPalette.primary.main,
                    color: KlpPalette.primary.main,
                    backgroundColor: KlpPalette.background.default,
                    '& svg': {
                        fill: KlpPalette.primary.main,
                    },
                },
            },
            containedSecondary: {
                color: KlpPalette.background.default,
                '&:hover': {
                    borderColor: KlpPalette.secondary.main,
                    color: KlpPalette.secondary.main,
                    backgroundColor: KlpPalette.background.default,
                    '& svg': {
                        fill: KlpPalette.secondary.main,
                    },
                },
            },
        },
        MuiGrid: {
            item: {
                display: 'flex',
                flexDirection: 'column',
            },
        },
        MuiAppBar: {
            root: {
                boxShadow: 'none',
            },
        },
        MuiTabs: {
            root: {
                borderBottom: '1px solid transparent',
                borderBottomColor: KlpPalette.primary.medium,
                color: KlpPalette.primary.dark,
                backgroundColor: KlpPalette.background.paper,
            },
        },
        MuiTab: {
            root: {
                textTransform: 'none',
                fontWeight: 400,
                fontSize: `${KlpFontVariant.epsilon.fontSize} !important`,
                lineHeight: 1.1,
                color: KlpPalette.primary.main,
                minWidth: 100,
                maxWidth: 200,
                transition: 'background-color 0.25s linear',
                '&:hover': {
                    backgroundColor: KlpPalette.secondary.light,
                },
            },
            textColorSecondary: {
                color: KlpPalette.primary.dark,
                '&$selected': {
                    color: KlpPalette.secondary.dark,
                    fontWeight: 500,
                },
            },
        },
        MuiCheckbox: {
            root: {
                '&:hover': {
                    backgroundColor: 'transparent !important',
                },
            },
        },
        MuiChip: {
            root: {
                lineHeight: '125%',
                height: 'auto',
            },
            outlinedSecondary: {
                borderWidth: '2px',
                color: KlpPalette.primary.main,
            },
            label: {
                paddingLeft: '6px',
                paddingRight: '6px',
            },
            clickableColorSecondary: {
                '&:hover, &:active': {
                    backgroundColor: `${KlpPalette.secondary.main} !important`,
                    color: KlpPalette.background.default,
                    cursor: 'pointer',
                },
            },
        },
        MUIDataTable: {
            paper: {
                boxShadow: shadows[1],
                backdropFilter: 'blur(1px)',
            },
        },

        MUIDataTableSelectCell: {
            headerCell: {
                backgroundColor: `${KlpPalette.secondary.veryLight}`,
            },
        },
        MUIDataTableHeadCell: {
            fixedHeader: {
                backgroundColor: `${KlpPalette.secondary.veryLight}`,
            },
        },
        MUIDataTableFooter: {
            root: {
                '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                    borderBottom: 'none',
                },
            },
        },
        MuiStepper: {
            root: {
                '& .MuiStepIcon-active': { color: KlpPalette.secondary.main },
                '& .MuiStepIcon-completed': {
                    color: KlpPalette.secondary.main,
                },
            },
        },

        MuiListItemText: {
            root: {
                lineHeight: KlpFontVariant.small.lineHeight,
                '& .MuiTypography-root': {
                    fontSize: KlpFontVariant.small.fontSize,
                    lineHeight: KlpFontVariant.small.lineHeight,
                },
            },
        },
        MuiListSubheader: {
            root: {
                color: KlpPalette.secondary.dark,
                fontSize: KlpFontVariant.small.fontSize,
                lineHeight: KlpFontVariant.small.lineHeight,
                paddingTop: '1em',
                paddingBottom: '1em',
                backgroundColor: KlpPalette.primary.veryLight,
            },
        },
    },
};

/*
    // Copied from KLP CSS definition

    // @name KLP Primary Morning Red;
    $klp-theme-color-02: #e41b65;
    $klp-theme-color-02--dark: #b22852;
    $klp-theme-color-02--medium: #ffd1d1;
    $klp-theme-color-02--light: #ffe1e1;

    // @name KLP Secondary Granite Gray;
    $klp-theme-color-01: #3d3d3d;
    $klp-theme-color-01--dark: #2e2e2e;
    $klp-theme-color-01--medium-dark: #737373;
    $klp-theme-color-01--medium: #b3b3b3;
    $klp-theme-color-01--medium-light: #e1e1e1;
    $klp-theme-color-01--light: #f0f0f0;
    $klp-theme-color-01--very-light: #f9f9f9;

    // @name KLP Primary Mountain Green;
    $klp-theme-color-03: #0db28a;
    $klp-theme-color-03--dark: #06735c;
    $klp-theme-color-03--medium: #cdfae2;
    $klp-theme-color-03--light: #e3f9ed;

    // @name KLP White;
    $klp-color-white: #ffffff;

    // @name KLP Custom
    $klp-color-go-ben: #72664d;
    $klp-color-sandal: #a5926a;
    $klp-color-neon-carrot: #ff9647;
    $klp-color-bright-sun: #ffc946;
    $klp-color-teal-blue: #084e70;
    $klp-color-astral: #2f70a2;
    $klp-color-dodger-blue: #3fa7ff;
    $klp-color-seagull: #80bfea;
    $klp-color-quinary-medium: #ffe19b;

    // @name KLP Error
    $klp-color-error: $klp-theme-color-02;
    $klp-color-error--lighter: $klp-theme-color-02--light;

    // @name Block text color primary
    $klp-block-txt-color-primary: $klp-color-white;
    $klp-block-txt-color-primary-dark: $klp-color-white;
    $klp-block-txt-color-primary-medium: $klp-theme-color-01;
    $klp-block-txt-color-primary-light: $klp-theme-color-01;

    // @name Block text color secondary
    $klp-block-txt-color-secondary: $klp-color-white;
    $klp-block-txt-color-secondary-dark: $klp-color-white;
    $klp-block-txt-color-secondary-medium-dark: $klp-color-white;
    $klp-block-txt-color-secondary-medium: $klp-theme-color-01;
    $klp-block-txt-color-secondary-medium-light: $klp-theme-color-01;
    $klp-block-txt-color-secondary-light: $klp-theme-color-01;
    $klp-block-txt-color-secondary-very-light: $klp-theme-color-01;

    // @name Block text color secondary
    $klp-block-txt-color-secondary: $klp-theme-color-01;
    $klp-block-txt-color-secondary-dark: $klp-color-white;
    $klp-block-txt-color-secondary-medium: $klp-theme-color-01;
    $klp-block-txt-color-secondary-light: $klp-theme-color-01;
*/

export const theme = createTheme(themeOptions);
